import {
  getTemplatePreview,
  listTemplates,
  type PuckTemplateSummary,
} from "@/api/cms-puck/templates";
import { createPuckPageFromTemplate } from "@/api/cms-puck/api";
import { hydrate } from "@/api/cms-puck/content-i18n";
import { createPuckConfig } from "@/config/cms-puck/config";
import { type SampleArtworkCard } from "@/components/cms-puck/blocks/catalogue-previews";
import { DEFAULT_SELECTION_VISIBILITY } from "@/lib/cms-puck/blocks/selection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from "@/components/ui/dialog";
import SearchBar from "@/components/ui/searchbar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Render, type Data } from "@measured/puck";
import { Check, Eye, LayoutTemplate } from "lucide-react";
import {
  Component,
  type ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import { useTranslation } from "react-i18next";

/** Isole un échec de rendu d'un bloc pour ne pas casser toute la modale. */
class PreviewErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

/** Largeur « bureau » de référence pour le rendu mis à l'échelle des vignettes. */
const RENDER_BASE_WIDTH = 1200;

/** Liens de navigation représentatifs (placeholder) pour le chrome d'aperçu. */
/** Libellés de navigation « factice » de l'aperçu (clés GlobalI18n `…preview.nav*`). */
function usePreviewNav() {
  const cmsNew = useCmsNew();
  return {
    home: cmsNew("cms.new.templates.preview.navHome", "Accueil"),
    artist: cmsNew("cms.new.templates.preview.navArtist", "Artiste"),
    works: cmsNew("cms.new.templates.preview.navWorks", "Œuvres"),
    news: cmsNew("cms.new.templates.preview.navNews", "Actualités"),
    about: cmsNew("cms.new.templates.preview.navAbout", "À propos"),
    contact: cmsNew("cms.new.templates.preview.navContact", "Contact"),
  };
}

/**
 * En-tête de site « factice » mais représentatif (logo + navigation + actions),
 * rendu au-dessus du contenu du modèle pour que la vignette ressemble à une vraie
 * page. Libellés via GlobalI18n → suivent la langue. Rendu à 1200px.
 */
function PreviewHeader() {
  const { i18n } = useTranslation();
  const nav = usePreviewNav();
  const items = [nav.home, nav.artist, nav.works, nav.news, nav.about, nav.contact];
  const langCode = (i18n.language || "fr").split("-")[0].toUpperCase();
  return (
    <header className="flex items-center justify-between border-b border-slate-200 bg-white px-10 py-5">
      <div className="h-7 w-32 rounded bg-slate-200" aria-hidden />
      <nav className="flex items-center gap-7">
        {items.map((l, i) => (
          <span key={i} className="text-sm font-semibold text-slate-700">
            {l}
          </span>
        ))}
      </nav>
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-slate-500">{langCode}</span>
        <span className="h-5 w-5 rounded-full bg-slate-200" aria-hidden />
      </div>
    </header>
  );
}

/** Pied de page « factice » représentatif (colonnes + réseaux + copyright). */
function PreviewFooter() {
  const cmsNew = useCmsNew();
  const nav = usePreviewNav();
  const items = [nav.home, nav.news, nav.about, nav.contact];
  return (
    <footer className="mt-10 border-t border-slate-200 bg-slate-50 px-10 py-10 text-slate-500">
      <div className="grid grid-cols-3 gap-8">
        <div className="flex flex-col gap-2">
          <div className="h-6 w-28 rounded bg-slate-200" aria-hidden />
          <div className="h-2.5 w-40 rounded bg-slate-200/70" aria-hidden />
          <div className="h-2.5 w-32 rounded bg-slate-200/70" aria-hidden />
        </div>
        <div className="flex flex-col items-center gap-2">
          {items.map((l, i) => (
            <span key={i} className="text-sm">
              {l}
            </span>
          ))}
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="flex gap-2">
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-6 w-6 rounded-full bg-slate-200" aria-hidden />
            ))}
          </div>
          <span className="text-xs">
            {cmsNew(
              "cms.new.templates.preview.copyright",
              "© 2026 — Tous droits réservés",
            )}
          </span>
        </div>
      </div>
    </footer>
  );
}

/**
 * Rend un layout Puck à sa largeur bureau puis le met à l'échelle pour tenir
 * dans le conteneur → vignette fidèle aux vraies proportions (héros 2 colonnes,
 * grille, etc.). `maxHeight` rogne le rendu (vignette de carte) ; sans lui, la
 * hauteur suit le contenu (aperçu défilable). `chrome` encadre le contenu d'un
 * en-tête + pied de page représentatifs (aperçu = vraie page).
 */
function ScaledRender({
  data,
  config,
  maxHeight,
  chrome = true,
}: {
  data: Data;
  config: ReturnType<typeof createPuckConfig>;
  maxHeight?: number;
  chrome?: boolean;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.25);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const update = () => {
      const s = outer.clientWidth / RENDER_BASE_WIDTH;
      setScale(s);
      setHeight(inner.scrollHeight * s);
    };
    const ro = new ResizeObserver(update);
    ro.observe(outer);
    ro.observe(inner);
    update();
    return () => ro.disconnect();
  }, [data]);

  const boxHeight = maxHeight ? Math.min(height || maxHeight, maxHeight) : height;

  return (
    <div
      ref={outerRef}
      style={{ width: "100%", height: boxHeight || undefined, overflow: "hidden" }}
    >
      <div
        ref={innerRef}
        className="pointer-events-none [&_a]:pointer-events-none"
        style={{ width: RENDER_BASE_WIDTH, transform: `scale(${scale})`, transformOrigin: "top left" }}
      >
        {chrome && <PreviewHeader />}
        <Render config={config} data={data} />
        {chrome && <PreviewFooter />}
      </div>
    </div>
  );
}

/** Base des images d'exemple servies statiquement (BO `public/cms-samples/`). */
const SAMPLE_IMAGE_BASE = `${import.meta.env.BASE_URL}cms-samples/`;
const sampleImageUrl = (name: string) => `${SAMPLE_IMAGE_BASE}${name}.jpg`;

/**
 * Images d'exemple assignées aux blocs image de chaque modèle — APERÇU UNIQUEMENT.
 * Choisies pour être cohérentes avec l'emplacement (portraits verticaux pour les
 * héros artiste, carrés pour la grille boutique, etc.). Ces images ne sont JAMAIS
 * écrites dans une page créée : elles sont injectées à la volée dans le layout
 * d'aperçu (cf. `injectSampleImages`) et affichées via l'échappatoire `sampleUrl`
 * des composants d'aperçu (MediaPreview / GalleryBlockPreview), sans résolution
 * d'Attachment d'instance.
 */
/** Œuvres d'exemple partagées (Sélection + aperçu CR). */
const SAMPLE_ARTWORK_CARDS: SampleArtworkCard[] = [
  {
    imageUrl: sampleImageUrl("vertical-01-gestuel"),
    title: "Sans titre (gestuel)",
    author: "Nom de l'artiste",
    date: "1962",
  },
  {
    imageUrl: sampleImageUrl("horizontal-03-composition"),
    title: "Composition",
    author: "Nom de l'artiste",
    date: "1958",
  },
  {
    imageUrl: sampleImageUrl("carre-02-flux"),
    title: "Flux",
    author: "Nom de l'artiste",
    date: "1965",
  },
];

/** Bloc Puck brut (aperçu). */
type PreviewNode = { type: string; props: Record<string, unknown> };

/**
 * Blocs d'exemple AJOUTÉS à l'aperçu du modèle « Catalogue raisonné » (hero + 2
 * boutons seulement, parité ancien CR) : une grille d'œuvres d'exemple
 * (ArtworkSelection avec `sampleCards`). APERÇU UNIQUEMENT — non persisté.
 */
const CR_PREVIEW_BLOCKS: PreviewNode[] = [
  {
    type: "ArtworkSelection",
    props: {
      id: "sample-cr-oeuvres",
      items: [],
      columns: 3,
      displayMode: "grid",
      alignment: "grid",
      captionAlign: "left",
      visibility: { ...DEFAULT_SELECTION_VISIBILITY },
      sampleCards: SAMPLE_ARTWORK_CARDS,
    },
  },
];

const SAMPLE_SELECTION_CATEGORIES = [
  {
    title: "Color Field",
    description: "Exploration de la couleur et de la forme.",
    imageUrl: sampleImageUrl("carre-01-bauhaus"),
  },
  {
    title: "Œuvres majeures",
    description: "Pièces emblématiques de la période.",
    imageUrl: sampleImageUrl("carre-02-flux"),
  },
  {
    title: "Études",
    description: "Travaux préparatoires et variations.",
    imageUrl: sampleImageUrl("carre-03-ondes"),
  },
];

const TEMPLATE_SAMPLE_IMAGES: Record<
  string,
  {
    textImages?: string[];
    gallery?: string[];
    /** Œuvres d'exemple pour un bloc ArtworkSelection (Sélection d'œuvres). */
    artworkSelection?: SampleArtworkCard[];
    /** Rubriques d'exemple pour SelectionSections. */
    selectionCategories?: typeof SAMPLE_SELECTION_CATEGORIES;
    /** Blocs d'exemple ajoutés à l'aperçu (ex. CR : découpages + œuvres). */
    appendBlocks?: PreviewNode[];
  }
> = {
  accueil: { textImages: ["vertical-01-gestuel"] },
  "fiche-artiste": { textImages: ["vertical-02-truchet"] },
  "a-propos": { textImages: ["horizontal-03-composition"] },
  contact: { textImages: ["vertical-03-marbre"] },
  boutique: {
    gallery: ["carre-01-bauhaus", "carre-02-flux", "carre-03-ondes"],
  },
  selection: { selectionCategories: SAMPLE_SELECTION_CATEGORIES },
  catalogue: { appendBlocks: CR_PREVIEW_BLOCKS },
};

/**
 * Injecte les images d'exemple dans le layout d'APERÇU d'un modèle (copie, jamais
 * muté en place) : chaque bloc TextImage/Media reçoit un `sampleUrl` (dans l'ordre),
 * chaque Gallery un `sampleUrls`. Les modèles sans emplacement image sont renvoyés
 * tels quels.
 */
function injectSampleImages(key: string, data: Data): Data {
  const map = TEMPLATE_SAMPLE_IMAGES[key];
  if (!map) return data;
  const textImages = [...(map.textImages ?? [])];
  const gallery = map.gallery ?? [];
  const artworkSelection = map.artworkSelection ?? [];
  const selectionCategories = map.selectionCategories ?? [];
  const content = data.content.map((node) => {
    if (
      (node.type === "TextImage" || node.type === "Media") &&
      textImages.length > 0
    ) {
      return {
        ...node,
        props: { ...node.props, sampleUrl: sampleImageUrl(textImages.shift()!) },
      };
    }
    if (node.type === "Gallery" && gallery.length > 0) {
      return {
        ...node,
        props: { ...node.props, sampleUrls: gallery.map(sampleImageUrl) },
      };
    }
    if (node.type === "ArtworkSelection" && artworkSelection.length > 0) {
      return {
        ...node,
        props: { ...node.props, sampleCards: artworkSelection },
      };
    }
    if (node.type === "SelectionSections" && selectionCategories.length > 0) {
      return {
        ...node,
        props: { ...node.props, sampleCategories: selectionCategories },
      };
    }
    return node;
  });
  // Blocs d'exemple ajoutés (aperçu CR : découpages + œuvres).
  const withAppended = map.appendBlocks
    ? [...content, ...map.appendBlocks]
    : content;
  return { ...data, content: withAppended };
}

/** Vignette de carte = vrai rendu du modèle, mis à l'échelle et rogné en haut. */
function TemplateThumb({
  template,
  config,
  height,
}: {
  template: PuckTemplateSummary;
  config: ReturnType<typeof createPuckConfig>;
  height: number;
}) {
  const { i18n } = useTranslation();
  const uiLang = (i18n.language || "fr").split("-")[0];
  const q = useQuery({
    queryKey: ["cms-puck", "template-preview", template.key, uiLang] as const,
    queryFn: () => getTemplatePreview(template.key, uiLang),
    staleTime: 5 * 60_000,
  });
  const data = useMemo(
    () =>
      q.data
        ? injectSampleImages(
            template.key,
            hydrate(q.data.data, q.data.strings),
          )
        : null,
    [q.data, template.key],
  );
  const fallback = (
    <div className="p-3">
      <Wireframe template={template} variant="card" />
    </div>
  );
  if (!data) return fallback;
  return (
    <PreviewErrorBoundary fallback={fallback}>
      <div className="bg-white" style={{ height }}>
        <ScaledRender data={data} config={config} maxHeight={height} />
      </div>
    </PreviewErrorBoundary>
  );
}

const TEMPLATES_KEY = ["cms-puck", "templates"] as const;
const PAGES_KEY = ["cms-puck", "pages"] as const;

/** Teinte de vignette par modèle (wireframe décoratif — vraies vignettes en D3). */
const ACCENTS: Record<string, string> = {
  accueil: "#2563eb",
  "fiche-artiste": "#7c3aed",
  actualites: "#d97706",
  selection: "#0f766e",
  expertise: "#b45309",
  "a-propos": "#475569",
  contact: "#334155",
  catalogue: "#4338ca",
  boutique: "#16a34a",
};
const accentOf = (key: string) => ACCENTS[key] ?? "#008bd4";

/**
 * Recette d'architecture par modèle (cf. CMS_TEMPLATES_ARCHITECTURE.md) → la
 * vignette dessine la vraie structure de la page (héros 2 colonnes, grilles,
 * boutons…). Déterministe, sans dépendance runtime : juste à partir de la clé.
 */
type ArchRow =
  | { t: "hero"; imageLeft: boolean; sub?: boolean }
  | { t: "titleCenter" }
  | { t: "textCenter" }
  | { t: "section" }
  | { t: "featured" }
  | { t: "grid"; cols: number }
  | { t: "buttons"; n: number };

const ARCHITECTURE: Record<string, ArchRow[]> = {
  accueil: [{ t: "hero", imageLeft: true, sub: true }],
  "fiche-artiste": [
    { t: "hero", imageLeft: true },
    { t: "section" },
    { t: "section" },
    { t: "section" },
    { t: "section" },
  ],
  actualites: [
    { t: "titleCenter" },
    { t: "textCenter" },
    { t: "featured" },
    { t: "grid", cols: 3 },
  ],
  selection: [{ t: "titleCenter" }, { t: "textCenter" }, { t: "grid", cols: 4 }],
  expertise: [
    { t: "titleCenter" },
    { t: "textCenter" },
    { t: "buttons", n: 1 },
    { t: "section" },
  ],
  "a-propos": [
    { t: "hero", imageLeft: true },
    { t: "section" },
    { t: "section" },
  ],
  contact: [{ t: "hero", imageLeft: false }],
  catalogue: [{ t: "hero", imageLeft: true }, { t: "buttons", n: 2 }],
  boutique: [{ t: "titleCenter" }, { t: "textCenter" }, { t: "grid", cols: 3 }],
};

/** Vignette wireframe fidèle à l'architecture de la page. */
function Wireframe({
  template,
  variant,
}: {
  template: PuckTemplateSummary;
  variant: "card" | "preview";
}) {
  const accent = accentOf(template.key);
  const big = variant === "preview";
  const rows = ARCHITECTURE[template.key] ?? [
    { t: "titleCenter" } as ArchRow,
    { t: "textCenter" } as ArchRow,
  ];

  const bar = (w: string, tone: "dark" | "mid" | "light" = "mid", h = 6) => (
    <div
      className="rounded"
      style={{
        width: w,
        height: h,
        background:
          tone === "dark" ? "#94a3b8" : tone === "mid" ? "#cbd5e1" : "#e2e8f0",
      }}
    />
  );
  const img = (h?: number) => (
    <div
      className="rounded"
      style={{ height: h, flex: h ? undefined : 1, background: accent, opacity: 0.85 }}
    />
  );
  const pill = (w: string) => (
    <div
      className="rounded-full"
      style={{ width: w, height: big ? 14 : 10, background: accent, opacity: 0.9 }}
    />
  );

  const renderRow = (row: ArchRow, i: number) => {
    switch (row.t) {
      case "hero": {
        const content = (
          <div className="flex flex-1 flex-col justify-center gap-1.5">
            {bar(big ? "70%" : "60%", "dark", big ? 11 : 8)}
            {row.sub && bar("40%", "mid", big ? 8 : 6)}
            <div className="mt-0.5 flex flex-col gap-1">
              {bar("100%", "light")}
              {bar("90%", "light")}
              {big && bar("95%", "light")}
            </div>
          </div>
        );
        return (
          <div key={i} className="flex gap-3" style={{ height: big ? 130 : 70 }}>
            {row.imageLeft ? (
              <>
                {img()}
                {content}
              </>
            ) : (
              <>
                {content}
                {img()}
              </>
            )}
          </div>
        );
      }
      case "titleCenter":
        return (
          <div key={i} className="flex justify-center pt-1">
            {bar(big ? "45%" : "50%", "dark", big ? 12 : 9)}
          </div>
        );
      case "textCenter":
        return (
          <div key={i} className="flex flex-col items-center gap-1">
            {bar("70%", "light")}
            {bar("55%", "light")}
          </div>
        );
      case "section":
        return (
          <div key={i} className="flex flex-col gap-1.5">
            {bar("35%", "dark", big ? 9 : 7)}
            {bar("100%", "light")}
            {big && bar("92%", "light")}
          </div>
        );
      case "featured":
        return (
          <div
            key={i}
            className="rounded"
            style={{ height: big ? 70 : 40, background: accent, opacity: 0.75 }}
          />
        );
      case "grid":
        return (
          <div
            key={i}
            className="grid gap-1.5"
            style={{
              gridTemplateColumns: `repeat(${row.cols}, minmax(0,1fr))`,
              height: big ? 56 : 34,
            }}
          >
            {Array.from({ length: row.cols }).map((_, k) => (
              <div key={k} className="rounded bg-slate-200" />
            ))}
          </div>
        );
      case "buttons":
        return (
          <div key={i} className="flex gap-2">
            {Array.from({ length: row.n }).map((_, k) => (
              <span key={k}>{pill("28%")}</span>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="flex flex-col gap-2.5 rounded-md bg-slate-100 p-3"
      style={{ minHeight: big ? undefined : 120 }}
    >
      {rows.map((r, i) => renderRow(r, i))}
    </div>
  );
}

export default function CMSAdvancedModeles() {
  const cmsNew = useCmsNew();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Titre du set pour l'AFFICHAGE (bibliothèque) : le libellé EN CAPITALES, suivi
   * de `(clé)` tant que la clé n'est pas seedée — pour qu'on voie tout de suite
   * que c'est une clé de trad (parité avec le reste de la lib). La CLÉ garde sa
   * casse (lisible) ; seul le libellé est capitalisé. Une fois seedé, seul le
   * libellé traduit s'affiche.
   */
  const setTitleVisible = (tpl: PuckTemplateSummary) => {
    const label = t(tpl.set.titleKey, tpl.set.titleFr).toUpperCase();
    return i18n.exists(tpl.set.titleKey)
      ? label
      : `${label} (${tpl.set.titleKey})`;
  };

  /**
   * Titre du set PROPRE (sans l'annotation `(clé)`) : réservé au NOM PERSISTÉ de
   * la page créée → « BASIQUE - … » (on ne fige jamais une clé dans un nom réel).
   */
  const setTitleClean = (tpl: PuckTemplateSummary) =>
    t(tpl.set.titleKey, tpl.set.titleFr);

  /** Nom préfixé pour l'AFFICHAGE (clé visible) : « BASIQUE (…) - Home page ».
   *  `setTitleVisible` capitalise déjà le libellé (et garde la clé lisible). */
  const setPrefixedNameDisplay = (tpl: PuckTemplateSummary) =>
    `${setTitleVisible(tpl)} - ${tpl.name}`;

  /** Nom préfixé PROPRE pour la page créée : « BASIQUE - Home page ». */
  const setPrefixedNameClean = (tpl: PuckTemplateSummary) =>
    `${setTitleClean(tpl).toUpperCase()} - ${tpl.name}`;

  // La bibliothèque suit la langue d'INTERFACE du BO (comme les libellés cmsNew) :
  // noms, descriptions, tags et composants sont localisés côté API via `?lang`.
  const uiLang = (i18n.language || "fr").split("-")[0];

  const templatesQuery = useQuery({
    queryKey: [...TEMPLATES_KEY, uiLang] as const,
    queryFn: () => listTemplates(uiLang),
  });

  const [activeSet, setActiveSet] = useState<string>("__all__");
  const [activeTag, setActiveTag] = useState<string>("__all__");
  const [search, setSearch] = useState("");
  const [previewKey, setPreviewKey] = useState<string | null>(null);

  const templates = templatesQuery.data ?? [];

  const tags = useMemo(() => {
    const set = new Set<string>();
    for (const tpl of templates) for (const tag of tpl.tags) set.add(tag);
    return Array.from(set).sort();
  }, [templates]);

  /** Sets distincts (clé → titre), dérivés des modèles chargés. */
  const sets = useMemo(() => {
    const byKey = new Map<string, PuckTemplateSummary["set"]>();
    for (const tpl of templates) if (!byKey.has(tpl.set.key)) byKey.set(tpl.set.key, tpl.set);
    return Array.from(byKey.values());
  }, [templates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return templates.filter((tpl) => {
      const setOk = activeSet === "__all__" || tpl.set.key === activeSet;
      const tagOk = activeTag === "__all__" || tpl.tags.includes(activeTag);
      const searchOk =
        !q ||
        tpl.name.toLowerCase().includes(q) ||
        tpl.description.toLowerCase().includes(q);
      return setOk && tagOk && searchOk;
    });
  }, [templates, activeSet, activeTag, search]);

  const preview = previewKey
    ? templates.find((t) => t.key === previewKey) ?? null
    : null;

  // Config Puck (fonction pure, sans hooks) pour le rendu réel de l'aperçu.
  const puckConfig = useMemo(() => createPuckConfig(), []);

  // Données de rendu du modèle en cours d'aperçu (layout + contenu type).
  const previewDataQuery = useQuery({
    queryKey: ["cms-puck", "template-preview", previewKey, uiLang] as const,
    queryFn: () => getTemplatePreview(previewKey as string, uiLang),
    enabled: !!previewKey,
  });
  const hydratedPreview = useMemo(() => {
    const d = previewDataQuery.data;
    if (!d) return null;
    const hydrated = hydrate(d.data, d.strings);
    return previewKey ? injectSampleImages(previewKey, hydrated) : hydrated;
  }, [previewDataQuery.data, previewKey]);

  const createMutation = useMutation({
    mutationFn: (template: PuckTemplateSummary) =>
      createPuckPageFromTemplate({
        name: setPrefixedNameClean(template),
        templateKey: template.key,
      }),
    onSuccess: (page) => {
      queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      setPreviewKey(null);
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: () => {
      toast({
        title: cmsNew("cms.new.templates.createFailed", "Échec de la création depuis le modèle"),
        variant: "error",
      });
    },
  });

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.templates.title", "Bibliothèque de modèles")}
      subtitle={cmsNew(
        "cms.new.templates.subtitle",
        "Chaque modèle est une page complète, déjà composée et remplie d'un contenu type. Choisis-en un pour démarrer — tu pourras tout modifier ensuite dans l'éditeur.",
      )}
    >
      <div className="px-6 py-4">
        {/* Filtres */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setActiveTag("__all__")}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                activeTag === "__all__"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
              }`}
            >
              {cmsNew("cms.new.templates.tagAll", "Tous")}
            </button>
            {tags.map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => setActiveTag(tag)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTag === tag
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="w-60 max-w-full">
              <SearchBar
                search={search}
                onChangeSearch={setSearch}
                placeholder={cmsNew("cms.new.templates.searchPlaceholder", "Rechercher un modèle…")}
              />
            </div>
            {/* Sélecteur de set (collection de modèles), à droite de la recherche */}
            <Select value={activeSet} onValueChange={setActiveSet}>
              <SelectTrigger className="h-9 w-fit max-w-full bg-white [&>span]:line-clamp-none">
                <SelectValue
                  placeholder={cmsNew("cms.new.templates.setAll", "Tous les sets")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {cmsNew("cms.new.templates.setAll", "Tous les sets")}
                </SelectItem>
                {sets.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {cmsNew(s.titleKey as `cms.new.${string}`, s.titleFr)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {templatesQuery.isLoading ? (
          <p className="py-12 text-center text-sm text-slate-400">
            {cmsNew("cms.new.pages.loading", "Chargement…")}
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-16 text-center text-sm text-slate-500">
            {cmsNew("cms.new.templates.noResults", "Aucun modèle ne correspond.")}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((template) => (
              <div
                key={template.key}
                className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow hover:shadow-md"
              >
                <div className="overflow-hidden border-b border-slate-100 bg-slate-50">
                  <TemplateThumb template={template} config={puckConfig} height={160} />
                </div>
                <div className="flex flex-1 flex-col gap-2 p-4">
                  <div className="flex flex-wrap gap-1.5">
                    {template.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-[10px] uppercase">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <h3 className="text-sm font-semibold text-slate-900">{setPrefixedNameDisplay(template)}</h3>
                  <p className="flex-1 text-xs leading-relaxed text-slate-500">
                    {template.description}
                  </p>
                  <div className="mt-1 flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1.5"
                      onClick={() => setPreviewKey(template.key)}
                    >
                      <Eye size={14} />
                      {cmsNew("cms.new.templates.preview", "Aperçu")}
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1"
                      disabled={createMutation.isPending}
                      onClick={() => createMutation.mutate(template)}
                    >
                      {cmsNew("cms.new.templates.use", "Utiliser")}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Aperçu détaillé */}
      <Dialog
        open={!!preview}
        onOpenChange={(open) => {
          if (!open) setPreviewKey(null);
        }}
      >
        <DialogContent className="max-w-5xl overflow-hidden p-0">
          {preview && (
            // `w-full min-w-0` : cette ligne est un enfant du DialogContent (grid) —
            // sans `min-w-0` (min-width:auto par défaut), le rendu interne de 1200px
            // l'élargit et pousse la colonne droite hors de la modale.
            <div className="flex w-full min-w-0 max-h-[70vh] flex-col md:flex-row">
              {/* Aperçu réel : rendu Puck du layout mis à l'échelle (vraies proportions).
                  `min-w-0` + `overflow-hidden` : le rendu interne (1200px) NE force PAS
                  l'élargissement de la colonne → elle reste à 3/5 et la colonne droite
                  ne déborde plus de la modale. */}
              <div className="max-h-[45vh] w-full min-w-0 overflow-x-hidden overflow-y-auto bg-white md:max-h-[80vh] md:w-3/5">
                {previewDataQuery.isLoading || !hydratedPreview ? (
                  <div className="p-6">
                    <Wireframe template={preview} variant="preview" />
                  </div>
                ) : (
                  <PreviewErrorBoundary
                    fallback={
                      <div className="p-6">
                        <Wireframe template={preview} variant="preview" />
                      </div>
                    }
                  >
                    <ScaledRender data={hydratedPreview} config={puckConfig} />
                  </PreviewErrorBoundary>
                )}
              </div>
              {/* Infos + actions : largeur fixe (2/5), ne se laisse pas comprimer. */}
              <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 overflow-y-auto border-l border-slate-100 p-6 md:w-2/5">
                <div className="flex flex-wrap gap-1.5">
                  {preview.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-[10px] uppercase">
                      {tag}
                    </Badge>
                  ))}
                </div>
                <h2 className="text-lg font-semibold text-slate-900">{setPrefixedNameDisplay(preview)}</h2>
                <p className="text-sm leading-relaxed text-slate-500">{preview.description}</p>
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {cmsNew("cms.new.templates.componentsIncluded", "Composants inclus")}
                </p>
                <ul className="flex flex-col gap-2">
                  {preview.components.map((comp) => (
                    <li key={comp} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-sky-50 text-sky-600">
                        <Check size={12} />
                      </span>
                      {comp}
                    </li>
                  ))}
                </ul>
                <DialogFooter className="mt-auto pt-4">
                  <Button variant="outline" onClick={() => setPreviewKey(null)}>
                    {cmsPhrase(cmsNew, "cancel")}
                  </Button>
                  <Button
                    className="gap-1.5"
                    disabled={createMutation.isPending}
                    onClick={() => createMutation.mutate(preview)}
                  >
                    <LayoutTemplate size={15} />
                    {cmsNew("cms.new.templates.use", "Utiliser ce modèle")}
                  </Button>
                </DialogFooter>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </CmsAdminShell>
  );
}
